import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@koyomi/ui';
import { api, ApiError } from './api.ts';

export const adminKey = (...parts: unknown[]) => ['admin', ...parts] as const;
export function useAdminQuery<T>(path: string, query?: Record<string, string | number | boolean | undefined>, opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({ queryKey: adminKey(path, query), queryFn: () => api<T>(path, { query }), enabled: opts.enabled, refetchInterval: opts.refetchInterval, placeholderData: (p) => p });
}
export function useAdminSave<TInput, TOut = unknown>(fn: (input: TInput) => Promise<TOut>, invalidatePrefixes: string[] = [], opts: { silent?: boolean } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation('common');
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidatePrefixes.map((p) => qc.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'admin' && String(q.queryKey[1]).startsWith(p) })));
      if (!opts.silent) toast.push(t('status.saved'), 'success');
    },
    onError: (err) => { toast.push(err instanceof ApiError ? err.message : t('errors.INTERNAL'), 'danger'); },
  });
}
export const money = (v: number | null | undefined, locale = 'pt-BR') => v == null ? '—' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
export const num = (v: number | null | undefined, locale = 'pt-BR') => v == null ? '—' : new Intl.NumberFormat(locale).format(v);
/** Tokens em notação compacta (1,75 mi). */
export const tokens = (v: number | null | undefined, locale = 'pt-BR') => v == null ? '—' : new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 2 }).format(v);
export const when = (d: string | Date | null | undefined, locale = 'pt-BR', opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) => d ? new Intl.DateTimeFormat(locale, opts).format(new Date(d)) : '—';
/** Prompt simples de motivo (todas as ações do Admin exigem motivo). */
export function askReason(label: string): string | null { const r = window.prompt(label); return r && r.trim().length >= 3 ? r.trim() : null; }
